"use client";

import { usePathname } from "next/navigation";
import { LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useOverlay } from "@/components/ui/useOverlay";
import { setMobileNavOpen, useMobileNavOpen } from "./mobile-nav";
import { useSidebarOrder } from "@/components/sidebar-order";
import { COLLAPSED_WIDTH, EXPANDED_WIDTH, TRANSITION } from "./nav-config";
import { FadeLabel, SidebarTooltip } from "./parts";
import { SidebarHeader } from "./SidebarHeader";
import { SidebarNav } from "./SidebarNav";
import { useSidebarGroups } from "./useSidebarGroups";


export function Sidebar({
  permissions,
  isPending,
  kind = "MEMBER",
  sessionsUnlocked = false,
  isTrainer = false,
  signOutAction,
  badges = {},
  userId,
}: {
  permissions: string[];
  isPending?: boolean;
  kind?: "MEMBER" | "GUEST";
  sessionsUnlocked?: boolean;
  /** Trainer on at least one journey (any status): the Trainees list opens for them. */
  isTrainer?: boolean;
  signOutAction: () => Promise<void>;
  badges?: Record<string, number>;
  /** Keys the per-user sidebar order in localStorage. */
  userId?: string;
}) {
  const order = useSidebarOrder(userId);
  const pathname = usePathname();
  // The sidebar stays as the user left it on every page (owner, 2026-11-03: opening a project no longer collapses it).
  const [pinned, setPinned] = useState(true);
  const expanded = pinned;
  const groups = useSidebarGroups({ permissions, isPending, kind, sessionsUnlocked, isTrainer, badges, pathname });
  const mobileOpen = useMobileNavOpen();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeMobile = () => setMobileNavOpen(false);
  // Below lg the sidebar is an off-canvas drawer over the content: closed by default,
  // closes on Esc / scrim tap (useOverlay, scrim) and on every route change.
  useOverlay(mobileOpen, drawerRef, closeMobile);
  useEffect(() => setMobileNavOpen(false), [pathname]);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const f = () => mq.matches && setMobileNavOpen(false);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  const body = (expanded: boolean, setPinned: (v: boolean) => void) => (
    <>
        <SidebarHeader expanded={expanded} setPinned={setPinned} />

        <SidebarNav
          g={groups}
          order={order}
          pathname={pathname}
          permissions={permissions}
          isPending={isPending}
          kind={kind}
          badges={badges}
          expanded={expanded}
          userId={userId}
        />

        {/* Logout — pinned to the very bottom, below the scrollable nav
            list, so it's reachable regardless of how many nav items
            there are above it. This calls the same signOut() server
            action Navbar's dropdown already exposes (see layout.tsx),
            just via a second, always-visible entry point styled and
            collapsed exactly like every other nav item above. */}
        <form
          action={signOutAction}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 border-t border-line p-3"
        >
          <SidebarTooltip label="Log out" expanded={expanded}>
            <button
              type="submit"
              aria-label="Log out"
              className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-fg-soft transition-colors hover:bg-surface-muted hover:text-fg"
            >
              <LogOut
                size={18}
                className="shrink-0 text-muted transition-colors group-hover:text-accent"
              />
              <FadeLabel expanded={expanded}>Log out</FadeLabel>
            </button>
          </SidebarTooltip>
        </form>
    </>
  );

  return (
    // Outer box reserves real layout space: its own width — not the
    // overlay's — so unpinned hovering never reflows the content column.
    <>
    {mobileOpen && (
      <div className="fixed inset-0 z-overlay md:hidden">
        <div className="modal-backdrop-enter absolute inset-0 bg-black/40" onClick={closeMobile} aria-hidden />
        <div
          ref={drawerRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className="sheet-in absolute inset-y-0 left-0 flex w-[272px] max-w-[85vw] flex-col overflow-hidden bg-white shadow-xl outline-none dark:bg-chrome"
        >
          {body(true, (v) => !v && closeMobile())}
        </div>
      </div>
    )}
    <div
      className={`relative h-full shrink-0 transition-[width] z-base max-md:hidden ${TRANSITION}`}
      style={{ width: pinned ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
    >
      {/* No border-r: the sidebar reads as a distinct region through its
          background color alone, not a bordered box. The content panel's
          own left edge (see layout.tsx) is the one divider. `absolute`
          (not `relative`) is what lets the unpinned+hovering state grow
          past this box's own reserved width without pushing content —
          it's simply drawn on top, with a shadow while it's doing so.
          `overflow-hidden` here (not just on the outer box) is what
          clips the always-mounted labels while narrow — the actual
          mechanism behind the collapsed state, not conditional
          rendering. Mouse handlers live here, not on the outer box above:
          this element is the one that actually visually grows on hover,
          so its own hit area needs to match its own (possibly expanded)
          width — handlers on the still-narrow outer box would make the
          sidebar collapse the moment the cursor moved into the newly-
          revealed part of itself. When floating (unpinned + hovering),
          `rounded-r-xl` gives this box its own soft edge — it's sitting
          on top of the content panel's rounded top-left corner while
          it's open, hiding that corner for as long as the overlay is
          up. Rather than fight to keep that corner visible underneath a
          higher-z-index box (not really possible), the overlay reads as
          its own intentional floating panel instead of a hard rectangle
          plastered over a rounded one. */}
      <aside
        onClick={() => {
          if (!pinned) setPinned(true);
        }}
        className={`absolute top-0 left-0 flex h-full flex-col overflow-hidden bg-white transition-[width] ${TRANSITION} dark:bg-chrome ${
          expanded ? "w-[272px]" : "w-16"
        } ${!pinned ? "z-base cursor-pointer rounded-r-xl" : ""}`}
      >
        {body(expanded, setPinned)}
      </aside>
    </div>
    </>
  );
}
